import React, { useState, useEffect, useRef, useCallback } from 'react';
import { loadGoogleMaps } from '../utils/googleMaps';
import { getDeliveryRequest } from '../services/adminDeliveryService';
import { getStore } from '../services/adminStoreService';
import './DeliveryTrackingMap.css';

const formatDistance = (meters) => {
  if (meters == null) return '—';
  if (meters >= 1000) return `${(meters / 1000).toFixed(1)} km`;
  return `${meters.toFixed(0)} m`;
};

const formatDuration = (seconds) => {
  if (seconds == null) return '—';
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
};

const num = (v) => (v != null && v !== '' ? parseFloat(v) : null);

const DeliveryTrackingMap = ({ requestId, onClose }) => {
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [routeDistance, setRouteDistance] = useState(null);
  const [routeDuration, setRouteDuration] = useState(null);
  const [routeLabel, setRouteLabel] = useState('');
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeError, setRouteError] = useState(null);

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const polylineRef = useRef(null);
  const mapsApiRef = useRef(null);
  const mapInitializedRef = useRef(false);

  const fetchRequestData = useCallback(async () => {
    if (!requestId) return;
    try {
      const data = await getDeliveryRequest(requestId);
      setRequest(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching delivery request:', err);
      setError(err.message || 'Failed to load tracking');
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    fetchRequestData();
    const interval = setInterval(fetchRequestData, 5000);
    return () => clearInterval(interval);
  }, [fetchRequestData]);

  // Initialize Google Maps once when request data is available
  useEffect(() => {
    if (!request || !mapContainerRef.current || mapInitializedRef.current) return;

    let cancelled = false;
    loadGoogleMaps()
      .then((maps) => {
        if (cancelled || !mapContainerRef.current) return;
        mapsApiRef.current = maps;
        mapInitializedRef.current = true;

        const centerLat = num(request.dropLatitude) ?? -6.7924;
        const centerLng = num(request.dropLongitude) ?? 39.2083;

        // Defer until after browser paint so IntersectionObserver has a laid-out element
        requestAnimationFrame(() => {
          if (cancelled || !mapContainerRef.current) return;
          const map = new maps.Map(mapContainerRef.current, {
            center: { lat: centerLat, lng: centerLng },
            zoom: 12,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
          });
          mapRef.current = map;
        });
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load Google Maps');
      });

    return () => { cancelled = true; };
  }, [request]);

  // Update markers and route whenever request data changes
  useEffect(() => {
    if (!mapRef.current || !mapsApiRef.current || !request) return;
    updateMapMarkers();
    updateRouteLayer();
    fitMapBounds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  const updateMapMarkers = () => {
    const map = mapRef.current;
    const maps = mapsApiRef.current;
    if (!map || !maps || !request) return;

    // Clear existing markers
    Object.values(markersRef.current).forEach((m) => m?.setMap(null));
    markersRef.current = {};

    const s = (request.status || '').toLowerCase();

    // Pickup marker
    const pickLat = num(request.pickLatitude);
    const pickLng = num(request.pickLongitude);
    if (pickLat != null && pickLng != null && ['pending', 'confirm', 'accepted'].includes(s)) {
      const marker = new maps.Marker({
        position: { lat: pickLat, lng: pickLng },
        map,
        title: 'Pickup',
        label: { text: '📦', fontSize: '20px' },
        icon: {
          path: maps.SymbolPath.CIRCLE,
          scale: 14,
          fillColor: '#FF9800',
          fillOpacity: 1,
          strokeColor: '#fff',
          strokeWeight: 2,
        },
      });
      const infoWindow = new maps.InfoWindow({ content: '<strong>Pickup</strong>' });
      marker.addListener('click', () => infoWindow.open(map, marker));
      markersRef.current.pickup = marker;
    }

    // Drop marker
    const dropLat = num(request.dropLatitude);
    const dropLng = num(request.dropLongitude);
    if (dropLat != null && dropLng != null) {
      const marker = new maps.Marker({
        position: { lat: dropLat, lng: dropLng },
        map,
        title: 'Delivery',
        icon: {
          path: maps.SymbolPath.CIRCLE,
          scale: 14,
          fillColor: '#039855',
          fillOpacity: 1,
          strokeColor: '#fff',
          strokeWeight: 2,
        },
      });
      const infoWindow = new maps.InfoWindow({ content: '<strong>Delivery</strong>' });
      marker.addListener('click', () => infoWindow.open(map, marker));
      markersRef.current.drop = marker;
    }

    // Driver marker
    const driverLat = num(request.driverLatitude);
    const driverLng = num(request.driverLongitude);
    if (driverLat != null && driverLng != null && ['confirm', 'accepted', 'picked', 'delivering'].includes(s)) {
      const marker = new maps.Marker({
        position: { lat: driverLat, lng: driverLng },
        map,
        title: request.driverName || 'Driver',
        icon: {
          path: maps.SymbolPath.FORWARD_CLOSED_ARROW,
          scale: 7,
          fillColor: '#1D4ED8',
          fillOpacity: 1,
          strokeColor: '#fff',
          strokeWeight: 2,
        },
      });
      const content = `<strong>Driver</strong><br/>${request.driverName || 'On the way'}`;
      const infoWindow = new maps.InfoWindow({ content });
      marker.addListener('click', () => infoWindow.open(map, marker));
      markersRef.current.driver = marker;
    }
  };

  const updateRouteLayer = async () => {
    const map = mapRef.current;
    const maps = mapsApiRef.current;
    if (!map || !maps || !request) return;

    // Clear existing polyline
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }

    setRouteDistance(null);
    setRouteDuration(null);
    setRouteLabel('');
    setRouteError(null);

    const status = (request.status || '').toLowerCase();
    const goingToDriverPickup = ['confirm', 'accepted'].includes(status);
    const goingToDrop = ['picked', 'delivering', 'delivered', 'completed'].includes(status);

    let pickLat = num(request.pickLatitude);
    let pickLng = num(request.pickLongitude);

    // Fallback: if pickup coords missing, try store coordinates
    if ((pickLat == null || pickLng == null) && request.storeId) {
      try {
        const store = await getStore(request.storeId);
        if (store?.coordinates && Array.isArray(store.coordinates) && store.coordinates.length >= 2) {
          pickLng = num(store.coordinates[0]);
          pickLat = num(store.coordinates[1]);
        }
      } catch (_) {}
    }

    const dropLat = num(request.dropLatitude);
    const dropLng = num(request.dropLongitude);
    const driverLat = num(request.driverLatitude);
    const driverLng = num(request.driverLongitude);

    const hasDriver = driverLat != null && driverLng != null;
    const hasPickup = pickLat != null && pickLng != null;
    const hasDrop = dropLat != null && dropLng != null;

    let origin, destination, color, label;

    if (hasDriver && (goingToDriverPickup || goingToDrop)) {
      origin = { lat: driverLat, lng: driverLng };
      destination = goingToDrop ? { lat: dropLat, lng: dropLng } : { lat: pickLat, lng: pickLng };
      color = goingToDrop ? '#2E7D32' : '#FF9800';
      label = goingToDrop ? 'Driver → Drop' : 'Driver → Pickup';
    } else if (hasPickup && hasDrop) {
      origin = { lat: pickLat, lng: pickLng };
      destination = { lat: dropLat, lng: dropLng };
      color = '#6B7280';
      label = 'Pickup → Drop';
    } else {
      if (hasDrop && !hasPickup) {
        setRouteError('Pickup location missing. Add store coordinates or pickup location to see route.');
      }
      return;
    }

    setRouteLoading(true);
    try {
      const directionsService = new maps.DirectionsService();
      const result = await new Promise((resolve) => {
        directionsService.route(
          { origin, destination, travelMode: maps.TravelMode.DRIVING },
          (res, status) => resolve({ routes: res?.routes, status })
        );
      });

      if (result.status === 'OK') {
        const leg = result.routes[0].legs[0];
        setRouteDistance(leg.distance.value);
        setRouteDuration(leg.duration.value);
        setRouteLabel(label);

        const path = result.routes[0].overview_path;
        const polyline = new maps.Polyline({
          path,
          geodesic: true,
          strokeColor: color,
          strokeOpacity: 0.88,
          strokeWeight: 5.5,
          map,
        });
        polylineRef.current = polyline;
      } else {
        setRouteError(`No route found (${result.status})`);
      }
    } catch (err) {
      console.error('Route fetch error:', err);
      setRouteError(err.message || 'Failed to fetch route');
    } finally {
      setRouteLoading(false);
    }
  };

  const fitMapBounds = () => {
    const map = mapRef.current;
    const maps = mapsApiRef.current;
    if (!map || !maps || !request) return;

    const bounds = new maps.LatLngBounds();
    let hasPoints = false;

    if (num(request.pickLatitude) != null && num(request.pickLongitude) != null) {
      bounds.extend({ lat: num(request.pickLatitude), lng: num(request.pickLongitude) });
      hasPoints = true;
    }
    if (num(request.dropLatitude) != null && num(request.dropLongitude) != null) {
      bounds.extend({ lat: num(request.dropLatitude), lng: num(request.dropLongitude) });
      hasPoints = true;
    }
    if (num(request.driverLatitude) != null && num(request.driverLongitude) != null) {
      bounds.extend({ lat: num(request.driverLatitude), lng: num(request.driverLongitude) });
      hasPoints = true;
    }

    if (hasPoints) map.fitBounds(bounds, { top: 60, bottom: 60, left: 60, right: 60 });
  };

  const getStatusText = () => {
    const s = (request?.status || '').toLowerCase();
    switch (s) {
      case 'pending': return 'Waiting for driver';
      case 'confirm':
      case 'accepted': return 'Driver heading to pickup';
      case 'picked':
      case 'delivering': return 'On the way to delivery';
      case 'delivered':
      case 'completed': return 'Delivered';
      case 'canceled':
      case 'cancelled': return 'Cancelled';
      default: return request?.status || 'Unknown';
    }
  };

  const getStatusClass = () => {
    const s = (request?.status || '').toLowerCase();
    switch (s) {
      case 'pending': return 'status-pending';
      case 'confirm':
      case 'accepted':
      case 'picked':
      case 'delivering': return 'status-in-progress';
      case 'completed':
      case 'delivered': return 'status-completed';
      case 'canceled':
      case 'cancelled': return 'status-canceled';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className="admin-tracking-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-tracking-content">
          <div className="admin-tracking-loading">
            <div className="admin-tracking-spinner" />
            <p>Loading tracking...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !request) {
    return (
      <div className="admin-tracking-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-tracking-content">
          <div className="admin-tracking-error">
            <p>{error || 'Tracking not found'}</p>
            <button type="button" onClick={onClose} className="admin-tracking-btn-close">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-tracking-modal" onClick={(e) => e.stopPropagation()}>
      <div className="admin-tracking-content">
        <div className="admin-tracking-header">
          <div className="admin-tracking-info">
            <h2>Track Delivery</h2>
            <p className="admin-tracking-number">Tracking: {request.trackingNumber || request.$id}</p>
            <div className="admin-tracking-header-row">
              <span className={`admin-tracking-status ${getStatusClass()}`}>{getStatusText()}</span>
              {routeLabel && (
                <span className="admin-tracking-route-label">
                  {routeLabel}
                  {routeLoading && <span className="admin-tracking-route-spinner" />}
                </span>
              )}
            </div>
          </div>
          <button type="button" onClick={onClose} className="admin-tracking-close-icon" aria-label="Close">
            ×
          </button>
        </div>
        <div className="admin-tracking-map" ref={mapContainerRef} />
        <div className="admin-tracking-metrics">
          <h3>Package tracking</h3>
          {routeError && (
            <p className="admin-tracking-route-error">{routeError}</p>
          )}
          {!(request.driverLatitude && request.driverLongitude) && !routeLabel && !routeError ? (
            <p className="admin-tracking-waiting">Waiting for driver location updates...</p>
          ) : routeLabel ? (
            <div className="admin-tracking-metrics-grid">
              <div className="admin-tracking-metric">
                <span className="admin-tracking-metric-label">Estimated time</span>
                <span className="admin-tracking-metric-value">{formatDuration(routeDuration)}</span>
              </div>
              <div className="admin-tracking-metric">
                <span className="admin-tracking-metric-label">Distance left</span>
                <span className="admin-tracking-metric-value">{formatDistance(routeDistance)}</span>
              </div>
            </div>
          ) : null}
          {request.driverName && (
            <div className="admin-tracking-driver-inline">
              <strong>Driver:</strong> {request.driverName}
              {request.driverContact && ` • ${request.driverContact}`}
              {request.vehicleNumber && ` • ${request.vehicleNumber}`}
            </div>
          )}
        </div>
        <div className="admin-tracking-actions">
          <button type="button" onClick={fitMapBounds} className="admin-tracking-btn-fit">
            Fit View
          </button>
          <button type="button" onClick={fetchRequestData} className="admin-tracking-btn-refresh">
            Refresh
          </button>
          <button
            type="button"
            onClick={updateRouteLayer}
            className="admin-tracking-btn-route"
            disabled={routeLoading}
          >
            {routeLoading ? 'Loading…' : 'Refresh route'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeliveryTrackingMap;
